import { useCallback } from "react"

interface ColumnResizeHandleProps {
  /** Called on each pointer move with width = startWidth + (clientX - startX). */
  onResize: (nextWidthPx: number) => void
  /** Called when the drag ends with the last clamped width (for persistence). */
  onResizeEnd?: (finalWidthPx: number) => void
  min: number
  max: number
  startWidth: number
}

/**
 * Drag handle on the right edge of a table header cell to resize the column.
 * Supports mouse and touch for mobile table layouts.
 */
export function ColumnResizeHandle({
  onResize,
  onResizeEnd,
  min,
  max,
  startWidth,
}: ColumnResizeHandleProps) {
  const beginResize = useCallback(
    (startX: number) => {
      const width0 = startWidth
      let last = width0

      const applyMove = (clientX: number) => {
        const next = Math.min(max, Math.max(min, width0 + (clientX - startX)))
        last = next
        onResize(next)
      }

      const onMouseMove = (ev: MouseEvent) => applyMove(ev.clientX)

      const onTouchMove = (ev: TouchEvent) => {
        ev.preventDefault()
        const touch = ev.touches[0]
        if (touch) applyMove(touch.clientX)
      }

      const end = () => {
        document.removeEventListener("mousemove", onMouseMove)
        document.removeEventListener("mouseup", end)
        document.removeEventListener("touchmove", onTouchMove)
        document.removeEventListener("touchend", end)
        document.removeEventListener("touchcancel", end)
        document.body.style.removeProperty("cursor")
        document.body.style.removeProperty("user-select")
        document.body.style.removeProperty("touch-action")
        onResizeEnd?.(last)
      }

      document.body.style.cursor = "col-resize"
      document.body.style.userSelect = "none"
      document.body.style.touchAction = "none"
      document.addEventListener("mousemove", onMouseMove)
      document.addEventListener("mouseup", end)
      document.addEventListener("touchmove", onTouchMove, { passive: false })
      document.addEventListener("touchend", end)
      document.addEventListener("touchcancel", end)
    },
    [max, min, onResize, onResizeEnd, startWidth],
  )

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      beginResize(e.clientX)
    },
    [beginResize],
  )

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const touch = e.touches[0]
      if (touch) beginResize(touch.clientX)
    },
    [beginResize],
  )

  return (
    <button
      type="button"
      aria-label="Resize column"
      tabIndex={-1}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      className="absolute right-0 top-0 z-20 h-full w-5 cursor-col-resize touch-manipulation border-0 bg-transparent p-0 after:absolute after:inset-y-1 after:right-1 after:w-px after:bg-[#8b9aff]/40 hover:bg-[#8b9aff]/25 md:w-2 md:after:content-none"
    />
  )
}
